import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "prosemirror-state";

const pluginKey = new PluginKey("autoLangSpellcheck");

const HEBREW_RE = /[\u0590-\u05FF]/g;
const LATIN_RE = /[A-Za-z]/g;

function countMatches(re, text) {
  const m = text.match(re);
  return m ? m.length : 0;
}

function detectLangAndDir(text) {
  const s = String(text || "").trim();
  if (!s) return null;

  const heb = countMatches(HEBREW_RE, s);
  const lat = countMatches(LATIN_RE, s);

  // If there is no strong signal, don't force anything.
  if (heb === 0 && lat === 0) return null;

  // Pure Hebrew (or Hebrew + numbers/punctuation) should always be Hebrew.
  if (heb > 0 && lat === 0) {
    return { lang: "he", dir: "rtl" };
  }

  // Small bias toward Hebrew only when it clearly dominates.
  // Prevents flipping due to a single Hebrew character inside English.
  const total = heb + lat;
  const hebRatio = total ? heb / total : 0;

  if (heb >= 2 && hebRatio >= 0.55) {
    return { lang: "he", dir: "rtl" };
  }

  // Default to English for Latin-heavy or mixed content.
  return { lang: "en", dir: "ltr" };
}

function computeChangedRanges(transaction) {
  // Gather approximate changed ranges from the mapping. This is "good enough"
  // for tagging blocks and avoids scanning the whole doc on each keystroke.
  const ranges = [];
  for (const map of transaction.mapping.maps) {
    map.forEach((from, to, newFrom, newTo) => {
      ranges.push({ from: newFrom, to: newTo });
    });
  }
  return ranges.length ? ranges : null;
}

export const AutoLangSpellcheck = Extension.create({
  name: "autoLangSpellcheck",

  addGlobalAttributes() {
    return [
      {
        types: ["paragraph", "heading", "listItem"],
        attributes: {
          lang: {
            default: null,
            parseHTML: (element) => element.getAttribute("lang") || null,
            renderHTML: (attrs) => (attrs.lang ? { lang: attrs.lang } : {}),
          },
          dir: {
            default: null,
            parseHTML: (element) => element.getAttribute("dir") || null,
            renderHTML: (attrs) => (attrs.dir ? { dir: attrs.dir } : {}),
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    const extensionThis = this;

    return [
      new Plugin({
        key: pluginKey,

        state: {
          init() {
            return { pending: [], timer: null };
          },
          apply(tr, value) {
            // Never react to our own attribute-update transaction.
            if (tr.getMeta(pluginKey) || tr.getMeta("autoLangSpellcheck")) {
              return value;
            }

            if (!tr.docChanged) return value;

            const next = { ...value };
            const ranges = computeChangedRanges(tr);
            if (ranges) next.pending = next.pending.concat(ranges);
            return next;
          },
        },

        view(editorView) {
          let destroyed = false;

          const schedule = () => {
            if (destroyed) return;
            const state = pluginKey.getState(editorView.state);
            if (!state?.pending?.length) return;

            // Debounce updates to keep typing smooth.
            if (state.timer) return;

            state.timer = window.setTimeout(() => {
              try {
                const current = pluginKey.getState(editorView.state);
                if (!current?.pending?.length) return;

                // Merge and normalize ranges.
                const pending = current.pending;
                current.pending = [];
                if (current.timer) {
                  clearTimeout(current.timer);
                  current.timer = null;
                }

                const doc = editorView.state.doc;
                const toProcess = [];

                for (const r of pending) {
                  const from = Math.max(0, Math.min(r.from, doc.content.size));
                  const to = Math.max(0, Math.min(r.to, doc.content.size));
                  toProcess.push({ from: Math.min(from, to), to: Math.max(from, to) });
                }

                // Expand small changes to cover full parent block nodes.
                const blocks = new Map(); // pos -> { node }
                for (const { from, to } of toProcess) {
                  doc.nodesBetween(from, to, (node, pos) => {
                    if (
                      node.type.name === "paragraph" ||
                      node.type.name === "heading" ||
                      node.type.name === "listItem"
                    ) {
                      blocks.set(pos, node);
                      return false;
                    }
                    return true;
                  });
                }

                if (!blocks.size) return;

                let tr = editorView.state.tr;
                tr.setMeta(pluginKey, true);
                tr.setMeta("autoLangSpellcheck", true);

                for (const [pos, node] of blocks.entries()) {
                  const text = node.textContent || "";
                  const detected = detectLangAndDir(text);
                  if (!detected) continue;

                  const nextAttrs = {
                    ...node.attrs,
                    lang: detected.lang,
                    dir: detected.dir,
                  };

                  // Only update if it changes.
                  if (node.attrs.lang === nextAttrs.lang && node.attrs.dir === nextAttrs.dir) {
                    continue;
                  }

                  tr = tr.setNodeMarkup(pos, undefined, nextAttrs, node.marks);
                }

                if (tr.docChanged) {
                  editorView.dispatch(tr);
                }
              } catch (e) {
                // Never block typing; fail silently and try next time.
                // eslint-disable-next-line no-console
                console.warn("[AutoLangSpellcheck] update failed:", e);
              } finally {
                const current = pluginKey.getState(editorView.state);
                if (current?.timer) {
                  clearTimeout(current.timer);
                  current.timer = null;
                }
              }
            }, extensionThis.options?.debounceMs ?? 400);
          };

          // Run on any state update; plugin state decides if work is needed.
          return {
            update: schedule,
            destroy() {
              destroyed = true;
              const state = pluginKey.getState(editorView.state);
              if (state?.timer) {
                clearTimeout(state.timer);
                state.timer = null;
              }
            },
          };
        },
      }),
    ];
  },
});

export default AutoLangSpellcheck;

